import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { saveToken, clearToken, getToken } from "@/lib/api";

const AuthCtx = createContext({
  user: null, loading: true,
  refresh: () => {}, logout: () => {},
  loginWithToken: () => {},
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Skip /me check while Google OAuth hash is present — AuthCallback will resolve it.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    // If we have neither token nor any cookie likely, still call /me (server will return 401 fast).
    refresh();
  }, [refresh]);

  const loginWithToken = useCallback(async (token, userObj) => {
    saveToken(token);
    if (userObj) setUser(userObj); else await refresh();
  }, [refresh]);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    clearToken();
    setUser(null);
  }, []);

  return (
    <AuthCtx.Provider value={{ user, loading, refresh, logout, loginWithToken, hasToken: !!getToken() }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
